import { useEffect, useState } from "react";
import "primeicons/primeicons.css";
import { formatNumberID } from "../../../../../utils/FormatNumber";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { Skeleton } from "primereact/skeleton";
import { Button } from "primereact/button";

const OwnerDashboardGeneralRank = (props) => {
  const { data, filterValue, loading } = props;

  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);

  const [productRankCategory, setProductRankCategory] = useState("Quantity");
  const [serviceRankCategory, setServiceRankCategory] = useState("Quantity");

  useEffect(() => {
    if (data) {
      let productRankByQty = [];
      data?.ranking_product?.qty?.product?.map((item) => {
        productRankByQty.push({
          rank: item.rank,
          name: item.product_name,
          qty: item.qty_mtd,
        });
      });
      let productRankByRev = [];
      data?.ranking_product?.revenue?.product?.map((item) => {
        productRankByRev.push({
          rank: item.rank,
          name: item.product_name,
          revenue: item.revenue_mtd,
        });
      });

      let serviceRankByQty = [];
      data?.ranking_product?.qty?.service?.map((item) => {
        serviceRankByQty.push({
          rank: item.rank,
          name: item.product_name,
          qty: item.qty_mtd,
        });
      });
      let serviceRankByRev = [];
      data?.ranking_product?.revenue?.service?.map((item) => {
        serviceRankByRev.push({
          rank: item.rank,
          name: item.product_name,
          revenue: item.revenue_mtd,
        });
      });

      setProducts([productRankByQty, productRankByRev]);

      setServices([serviceRankByQty, serviceRankByRev]);
    }
  }, [data]);

  const RankItem = ({ item, index, type }) => {
    return (
      <div
        className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full px-4 py-1 rounded-lg shadow-sm ${
          item.rank <= 3
            ? "bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200"
            : "bg-gray-50"
        }`}
      >
        {/* Left Section: Rank + Name */}
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`min-w-[2rem] min-h-[2rem] w-[2rem] h-[2rem] rounded-full flex items-center justify-center text-white text-sm font-bold ${
              item.rank === 1
                ? "bg-gradient-to-r from-yellow-400 to-yellow-600"
                : item.rank === 2
                ? "bg-gradient-to-r from-gray-400 to-gray-600"
                : item.rank === 3
                ? "bg-gradient-to-r from-yellow-600 to-yellow-800"
                : "bg-gradient-to-r from-purple-500 to-blue-600"
            }`}
          >
            {item.rank <= 3 ? (
              <i className="pi pi-crown text-lg"></i>
            ) : (
              item.rank
            )}
          </span>

          {/* Name with ellipsis and tooltip */}
          <span
            className="text-sm sm:text-md font-semibold truncate sm:max-w-[500px]"
            title={item.name}
          >
            {item.name}
          </span>
        </div>

        {/* Right Section: Quantity or Revenue */}
        <span className="text-sm text-right sm:text-left sm:text-lg font-bold bg-gradient-to-br from-green-400 to-emerald-500 text-transparent bg-clip-text whitespace-nowrap">
          {type === "product"
            ? productRankCategory === "Quantity"
              ? item.qty
              : `Rp ${formatNumberID(item.revenue)}`
            : serviceRankCategory === "Quantity"
            ? item.qty
            : `Rp ${formatNumberID(item.revenue)}`}
        </span>
      </div>
    );
  };

  const RankItemSkeleton = () => {
    return (
      <div className="flex items-center justify-between gap-2 w-full px-4 py-1 rounded-lg shadow-sm bg-gray-50">
        {/* Left: Circle + Name */}
        <div className="flex items-center gap-2 min-w-0">
          <Skeleton shape="circle" width="2rem" height="2rem" />
          <Skeleton width="5rem" height="1rem" />
        </div>

        {/* Right: Number */}
        <Skeleton width="5rem" height="1rem" />
      </div>
    );
  };

  return (
    <div className="col-start-center w-full gap-4">
      <div className="elevated-card-tight-vertical w-full !shadow-md col-center-start gap-4 border border-gray-300 !px-[1rem] sm:!px-6">
        <div className="flex flex-col items-start justify-between lg:flex-row gap-2 w-full">
          <div className="flex flex-col items-start justify-between gap-2 w-full">
            <label className="row-start-center gap-2 text-xl font-semibold text-black">
              <i className="pi pi-trophy text-yellow-500 text-2xl"></i>
              Top 10 Packages by {productRankCategory}
            </label>
            <span className="text-xs bg-gradient-to-br from-green-200 to-emerald-200 text-black font-semibold border border-green-400 rounded-md px-2 py-0.5">
              {format(new Date(filterValue?.dari_tanggal), "dd MMM yyyy")} -{" "}
              {format(new Date(filterValue?.sampai_tanggal), "dd MMM yyyy")}
            </span>
          </div>

          {/* Toggle Buttons */}
          <div className="flex gap-2 rounded-md p-1 w-full lg:w-auto">
            {["Quantity", "Revenue"].map((option) => (
              <button
                key={option}
                onClick={() => setProductRankCategory(option)}
                className={`px-4 py-1.5 rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 ease-in-out w-full lg:w-auto border-none
          ${
            productRankCategory === option
              ? "bg-gradient-to-br from-green-400 to-emerald-500 text-white shadow"
              : "text-black hover:bg-green-100 bg-slate-100"
          }`}
                disabled={loading}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        <div className="col-center-start w-full gap-2">
          <AnimatePresence mode="wait">
            {productRankCategory === "Quantity" ? (
              <motion.div
                key="quantity"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="col-center-start w-full gap-2"
              >
                {loading ? (
                  [...Array(10)].map((_, idx) => <RankItemSkeleton key={idx} />)
                ) : products?.[0]?.length > 0 ? (
                  products?.[0]?.map((item, index) => (
                    <RankItem
                      key={`${item.name}-${item.rank}`}
                      item={item}
                      index={index}
                      type="product"
                    />
                  ))
                ) : (
                  <span className="text-lg text-black">
                    No data available...
                  </span>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="revenue"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="col-center-start w-full gap-2"
              >
                {loading ? (
                  [...Array(10)].map((_, idx) => <RankItemSkeleton key={idx} />)
                ) : products?.[1]?.length > 0 ? (
                  products?.[1]?.map((item, index) => (
                    <RankItem
                      key={`${item.name}-${item.rank}`}
                      item={item}
                      index={index}
                      type="product"
                    />
                  ))
                ) : (
                  <span className="text-lg text-black">
                    No data available...
                  </span>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <div className="elevated-card-tight-vertical w-full !shadow-md col-center-start gap-4 border border-gray-300  !px-[1rem] sm:!px-6">
        <div className="flex flex-col items-start justify-between lg:flex-row gap-2 w-full">
          <div className="flex flex-col items-start justify-between gap-2 w-full">
            <label className="row-start-center gap-2 text-xl font-semibold text-black">
              <i className="pi pi-trophy text-yellow-500 text-2xl"></i>
              Top 10 Services by {serviceRankCategory}
            </label>
            <span className="text-xs bg-gradient-to-br from-green-200 to-emerald-200 text-black font-semibold border border-green-400 rounded-md px-2 py-0.5">
              {format(new Date(filterValue?.dari_tanggal), "dd MMM yyyy")} -{" "}
              {format(new Date(filterValue?.sampai_tanggal), "dd MMM yyyy")}
            </span>
          </div>

          {/* Toggle Buttons */}
          <div className="flex gap-2 rounded-md p-1 w-full lg:w-auto">
            {["Quantity", "Revenue"].map((option) => (
              <button
                key={option}
                onClick={() => setServiceRankCategory(option)}
                className={`px-4 py-1.5 rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 ease-in-out w-full lg:w-auto
          ${
            serviceRankCategory === option
              ? "bg-gradient-to-br from-green-400 to-emerald-500 text-white shadow"
              : "text-black hover:bg-green-100 bg-slate-100"
          }`}
                disabled={loading}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
        <div className="col-center-start w-full gap-2">
          <AnimatePresence mode="wait">
            {serviceRankCategory === "Quantity" ? (
              <motion.div
                key="quantity"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="col-center-start w-full gap-2"
              >
                {loading ? (
                  [...Array(10)].map((_, idx) => <RankItemSkeleton key={idx} />)
                ) : services?.[0]?.length > 0 ? (
                  services?.[0]?.map((item, index) => (
                    <RankItem
                      key={`${item.name}-${item.rank}`}
                      item={item}
                      index={index}
                      type="service"
                    />
                  ))
                ) : (
                  <span className="text-lg text-black">
                    No data available...
                  </span>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="revenue"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="col-center-start w-full gap-2"
              >
                {loading ? (
                  [...Array(10)].map((_, idx) => <RankItemSkeleton key={idx} />)
                ) : services?.[1]?.length > 0 ? (
                  services?.[1]?.map((item, index) => (
                    <RankItem
                      key={`${item.name}-${item.rank}`}
                      item={item}
                      index={index}
                      type="service"
                    />
                  ))
                ) : (
                  <span className="text-lg text-black">
                    No data available...
                  </span>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
export default OwnerDashboardGeneralRank;
